import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ConfigAgendaCitas.scss';
import { useApiAppointments } from '../../api/api.appoiments';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import DataGrid, { Column, Button as Butt, Editing, Item as Items, Paging, Toolbar, Popup, Lookup, RequiredRule, Selection, SearchPanel, Scrolling, Form, PatternRule } from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { useAppSelector } from '../../contexts/hooks';
import { useScreenSize } from '../../utils/media-query';
import { Item } from 'devextreme-react/form';
import { SysI_Agenda } from '../../types/types.agendaCitas';
import { ColorBox } from 'devextreme-react/color-box';

const ConfigAppoiments = () => {
  const { isMedium, isSmall } = useScreenSize();
  const { getAgendas, createAgenda, updateAgenda, deleteAgenda } = useApiAppointments();
  const [data, setData] = useState<SysI_Agenda[]>([]);
  const dataGridRef = useRef<DataGrid>(null);

  // Obtener sedes y usuarios desde Redux
  const Sedes = useAppSelector((data) => data.configuration.Data?.ComboBox)?.filter((x) => x.Name === 'SysI_Sedes')[0]?.Data || [];
  const Usuarios = useAppSelector((data) => data.configuration.Data?.UsuariosAgendas) || [];

  // Función para formatear un tiempo como string HH:MM
  const formatTimeToString = (timeValue) => {
    if (!timeValue) return null;

    // Si ya es un string, verificamos su formato
    if (typeof timeValue === 'string') {
      // Si parece una fecha ISO, la convertimos
      if (timeValue.includes('T')) {
        const date = new Date(timeValue);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${hours}:${minutes}`;
      }
      // Si parece un string de tiempo HH:MM:SS, lo limpiamos
      if (timeValue.includes(':')) {
        return timeValue.split(':').slice(0, 2).join(':');
      }
      // De lo contrario, lo devolvemos como está
      return timeValue;
    }

    // Si es un objeto Date, lo formateamos
    if (timeValue instanceof Date) {
      const hours = String(timeValue.getHours()).padStart(2, '0');
      const minutes = String(timeValue.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    }

    return null;
  };

  // En useEffect
  useEffect(() => {
    getAgendas().then((res) => {
      // Procesar las horas como strings formateados, no como objetos Date
      const processedData = res.map((agenda) => ({
        ...agenda,
        // Convertir tiempos a strings en formato HH:MM
        Ag_HoraInicio: formatTimeToString(agenda.Ag_HoraInicio),
        Ag_HoraFin: formatTimeToString(agenda.Ag_HoraFin),
      }));

      setData(processedData);
    });
  }, []);
  const handleUpdateData = () => {
    getAgendas().then((res) => {
      setData(res);
    });
  };

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Ag_Id',
      loadMode: 'processed',
      load() {
        return getAgendas()
          .then((res) => ({
            data: res,
            totalCount: res.length,
          }))
          .catch(() => {
            throw new Error('Error al cargar datos de agendas');
          });
      },
      insert: async (values) => {
        try {
          // Formatear las horas correctamente (solo la parte del tiempo)
          const formattedValues = {
            ...values,
            Ag_HoraInicio: formatTimeToString(values.Ag_HoraInicio),
            Ag_HoraFin: formatTimeToString(values.Ag_HoraFin),
          };

          const newId = await createAgenda(formattedValues as SysI_Agenda);
          // Devolver el objeto completo como espera DevExtreme
          return { ...values, Ag_Id: newId };
        } catch (error) {
          throw new Error('Error al crear agenda');
        }
      },
      update: (key, values) => {
        const updatedItem = { ...values, Ag_Id: key };
        return updateAgenda(updatedItem)
          .then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error al actualizar agenda');
          });
      },
      remove: async (key) => {
        return deleteAgenda(key)
          .then((res) => {
            if (res) {
              dataGridRef.current?.instance.refresh();
            }
          })
          .catch(() => {
            throw new Error('Error al eliminar agenda');
          });
      },
    });
  }, [data]);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  // Renderizador personalizado para el ColorBox
  const colorBoxRender = (data) => {
    return (
      <div
        style={{
          backgroundColor: data.value || '#ffffff',
          width: '30px',
          height: '30px',
          borderRadius: '4px',
          border: '1px solid #ccc',
          margin: '0 auto', // Esto centrará el div horizontalmente
        }}></div>
    );
  };

  // Renderizador de editor para el ColorBox
  const colorBoxEditor = (cell) => {
    return <ColorBox defaultValue={cell.value} onValueChanged={(e) => cell.setValue(e.value)} />;
  };

  // Función para parsear una cadena de tiempo a un objeto Date (para mostrar en editores)
  const parseTimeToDate = (timeString) => {
    if (!timeString) return null;

    // Si es una fecha ISO, la dejamos como está
    if (timeString.includes('T') && timeString.includes('Z')) {
      return new Date(timeString);
    }

    // Si es solo un string de tiempo (HH:MM:SS), lo convertimos a Date
    const today = new Date();
    const [hours, minutes, seconds] = timeString.split(':').map(Number);

    today.setHours(hours || 0);
    today.setMinutes(minutes || 0);
    today.setSeconds(seconds || 0);
    today.setMilliseconds(0);

    return today;
  };

  // Renderizador simplificado para tiempo
  const timeRender = (data) => {
    if (!data.value) return <span>--:--</span>;

    // Formatear a HH:MM si es necesario
    const formattedTime = formatTimeToString(data.value);
    return <span>{formattedTime}</span>;
  };

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper view-wrapper-contact-list'>
        <DataGrid
          ref={dataGridRef}
          className='grid'
          dataSource={customDataSrc}
          height='100%'
          repaintChangesOnly={true}
          highlightChanges={true}
          rowAlternationEnabled={true}
          showColumnLines={true}
          onInitNewRow={(e) => {
            // Valores por defecto para un nuevo registro
            e.data.Ag_Estado = true;
            e.data.Ag_Color = '#1976d2';
            e.data.Ag_PeriodoCita = 30; // 30 minutos por defecto
          }}
          onEditorPreparing={(e) => {
            // Asegurar que el editor tenga todos los valores
            if (e.parentType === 'dataRow' && e.dataField) {
              // Manejar específicamente los campos de hora
              if (e.dataField === 'Ag_HoraInicio' || e.dataField === 'Ag_HoraFin') {
                e.editorOptions.onValueChanged = (args) => {
                  // Asegurar que el valor se asigne correctamente
                  const time = args.value;
                  if (time) {
                    //console.log(`Valor de ${e.dataField} cambiado a:`, time);
                    // Asignar el valor al campo del modelo
                    e.setValue(time);
                  }
                };
              }
            }
          }}>
          <SearchPanel visible={true} />
          <Scrolling columnRenderingMode='standard' mode='virtual' />
          <Editing
            mode='popup'
            allowAdding
            allowDeleting
            allowUpdating
            texts={{
              saveRowChanges: 'Guardar',
              cancelRowChanges: 'Cancelar',
              deleteRow: 'Eliminar',
              addRow: 'Agregar',
            }}>
            <Popup title='CONFIGURACIÓN DE AGENDA' width={900} height={'70%'} fullScreen={isMedium || isSmall} showTitle={true} />
            <Form>
              <Item dataField='Ag_Us_Id' editorType='dxSelectBox' />
              <Item dataField='Ag_Sd_Id' editorType='dxSelectBox' />
              <Item dataField='Ag_Nombre' />
              <Item dataField='Ag_Descripcion' />
              <Item dataField='Ag_Color' editorType='dxColorBox' />
              <Item
                dataField='Ag_HoraInicio'
                editorType='dxDateBox'
                editorOptions={{
                  type: 'time',
                  pickerType: 'list',
                  displayFormat: 'HH:mm',
                  interval: 15,
                  useMaskBehavior: true,
                  showClearButton: true,
                  onValueChanged: (e) => {
                    // Asignar directamente el valor al editor
                    console.log('Hora inicio cambiada:', e.value);
                  },
                }}
                label={{ text: 'Hora de Inicio' }}
                isRequired={true}
              />
              <Item
                dataField='Ag_HoraFin'
                editorType='dxDateBox'
                editorOptions={{
                  type: 'time',
                  pickerType: 'list',
                  displayFormat: 'HH:mm',
                  interval: 15,
                  useMaskBehavior: true,
                  showClearButton: true,
                  onValueChanged: (e) => {
                    console.log('Hora fin cambiada:', e.value);
                  },
                }}
                label={{ text: 'Hora de Fin' }}
                isRequired={true}
              />
              <Item
                dataField='Ag_PeriodoCita'
                editorType='dxNumberBox'
                editorOptions={{
                  min: 5,
                  max: 120,
                  step: 5,
                  showSpinButtons: true,
                }}
                label={{ text: 'Duración de Cita (minutos)' }}
                isRequired={true}
              />
              <Item dataField='Ag_Estado' editorType='dxCheckBox' label={{ text: 'Estado (Activo/Inactivo)' }} />
            </Form>
          </Editing>
          <Paging enabled={false} />
          <Selection mode='single' />
          <Toolbar>
            <Items location='before'>
              <div className='grid-header'>Configuración de Agendas</div>
            </Items>
            <Item name='searchPanel' />
            <Item name='addRowButton' />
            <Items location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
              <Button id='RefrescarAgendas' icon='refresh' text='Actualizar' stylingMode='text' onClick={handleUpdateData} />
              <Tooltip target={'#RefrescarAgendas'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Refrescar datos
              </Tooltip>
            </Items>
          </Toolbar>

          <Column caption='ID' dataField='Ag_Id' alignment='center' width={70} allowEditing={false} />

          <Column caption='USUARIO' allowEditing={true} dataField='Ag_Us_Id' alignment='center' width={150}>
            <Lookup dataSource={Usuarios} valueExpr='Us_Id' displayExpr='Us_Nombre' />
            <RequiredRule />
          </Column>

          <Column caption='SEDE' allowEditing={true} dataField='Ag_Sd_Id' alignment='center' width={150}>
            <Lookup dataSource={Sedes} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
            <RequiredRule />
          </Column>

          <Column caption='NOMBRE' allowEditing={true} dataField='Ag_Nombre' alignment='left'>
            <RequiredRule />
          </Column>

          <Column caption='DESCRIPCIÓN' allowEditing={true} dataField='Ag_Descripcion' alignment='left'></Column>

          <Column caption='COLOR' allowEditing={true} dataField='Ag_Color' alignment='center' width={100} cellRender={colorBoxRender} editCellRender={colorBoxEditor}>
            <RequiredRule />
          </Column>
          <Column caption='ESTADO' allowEditing={true} dataField='Ag_Estado' alignment='center' width={100}></Column>
          <Column caption='HORA INICIO' dataField='Ag_HoraInicio' alignment='center' width={110} dataType='datetime' format='HH:mm' cellRender={timeRender}>
            <RequiredRule message='La hora de inicio es obligatoria' />
          </Column>

          <Column caption='HORA FIN' dataField='Ag_HoraFin' alignment='center' width={110} dataType='datetime' format='HH:mm' cellRender={timeRender}>
            <RequiredRule message='La hora de fin es obligatoria' />
          </Column>

          <Column caption='DURACIÓN CITA' dataField='Ag_PeriodoCita' alignment='center' width={120} dataType='number' format='#0 min'>
            <RequiredRule message='La duración de cita es obligatoria' />
          </Column>
          <Column type='buttons' caption={'ACCIÓN'} width={120}>
            <Butt name='edit' />
            <Butt name='delete' />
          </Column>
        </DataGrid>
      </div>
    </div>
  );
};

export default ConfigAppoiments;
